import { Autocomplete } from "@mui/material";
import { useState, useEffect, useMemo, useReducer, SyntheticEvent } from "react";
import {
  generateRetrieveConnections,
  combineOptionsWithValue,
  calculateValue,
  generateOptionsReducer,
  formatOptionsWithRecordConverter,
  generateOnChange,
  generateFilterOptions,
  generateSelectAllOption,
} from "./helpers";
import { ConnectionAutocompleteProps, ConnectionAutocompleteRef, OptionsActionTypes, Option } from "./types";
import { generateRenderInput, generateRenderOption } from "./components";

const ConnectionAutocomplete = <T extends string, P extends boolean>(props: ConnectionAutocompleteProps<T, P>) => {
  const {
    crud,
    searchFields,
    defaultGetParams,
    convertRecordToOption,
    responseData,
    additionalOptions = [],
    value,
    renderInput,
    renderOption,
    dependency,
    setConnections,
    setTotalCount,
    tooltipMessage,
    onChange,
    freeSolo,
    lazyRetrieve,
    onOpen,
    filterOptions: manualFilterOptions,
    retrieveErrorHandler,
    loading: loadingProp = false,
    selectAllOption = false,
    ...autocompleteProps
  } = props;

  //* Determined values for state
  // calculate the initial options based on passed in options, value, and method of converting records to options
  const initialOptions = useMemo(() => combineOptionsWithValue({ options: additionalOptions, value }), []);
  const formattedInitialOptions = useMemo(() => formatOptionsWithRecordConverter({ options: initialOptions, convertRecordToOption }), []);

  const optionsReducer = generateOptionsReducer({ convertRecordToOption });

  //* State
  const [options, dispatchOptions] = useReducer(optionsReducer, formattedInitialOptions);
  const [inputValue, setInputValue] = useState<string>("");
  const [haveRetrievedAll, setHaveRetrievedAll] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialRetrieved, setInitialRetrieved] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [rendered, setRendered] = useState<boolean>(false);

  //* Variables and cached variables
  const connectionCrud = useMemo(() => crud(), [crud]);

  const calculatedValue = useMemo(() => {
    return calculateValue({ value, convertRecordToOption });
  }, [value, convertRecordToOption]);

  // Determine whether component is disabled
  const disabled = useMemo(() => {
    // if disabled based on Autocomplete props - it is disabled
    const isDisabled = autocompleteProps.disabled || false;

    if (isDisabled) return true;

    // if no dependency - is not disabled
    if (!dependency) return false;

    // if there is a dependency - the dependency is not active/fullilled and disabledWhenInactive is true
    const disabledBasedOnDependency = !dependency.fulfilled && dependency.disabledWhenUnfulfilled;

    return disabledBasedOnDependency;
  }, [dependency, autocompleteProps.disabled]);

  //* Generated methods
  const retrieveConnectionRecords = useMemo(
    () =>
      generateRetrieveConnections({
        crud: connectionCrud,
        haveRetrievedAll,
        setHaveRetrievedAll,
        defaultGetParams,
        inputValue,
        searchFields,
        responseData,
        convertRecordToOption,
        value: calculatedValue,
        dispatchOptions,
        setLoading,
        options,
        setTotalRecords,
        retrieveErrorHandler,
      }),
    [
      connectionCrud,
      haveRetrievedAll,
      setHaveRetrievedAll,
      defaultGetParams,
      inputValue,
      searchFields,
      responseData,
      convertRecordToOption,
      calculatedValue,
      dispatchOptions,
      setLoading,
      options,
      setTotalRecords,
      retrieveErrorHandler,
    ],
  );

  const filterOptions = useMemo(() => generateFilterOptions(), []);

  const tooltipTitle = useMemo(() => {
    if (!tooltipMessage) return tooltipMessage;

    if (typeof tooltipMessage === "string") return tooltipMessage;

    const calculatedTooltipMessage = tooltipMessage(options);

    return calculatedTooltipMessage;
  }, [tooltipMessage, options]);

  const customRenderInput = useMemo(
    () => generateRenderInput({ renderInput, loading, dependency, tooltipMessage: tooltipTitle, haveRetrievedAll, value, disabled, loadingProp }),
    [renderInput, loading, dependency, tooltipTitle, haveRetrievedAll, value, loadingProp],
  );

  const customOnOpen = async (event: SyntheticEvent) => {
    // When using lazyRetrieve - wait to retrieve initial records until interacted with (onOpen) - if already retrieved initial - do not retrieve initial again
    if (initialRetrieved || !lazyRetrieve) {
      onOpen && onOpen(event);
      return;
    }

    const dependencyFulfilled = dependency?.fulfilled !== undefined ? dependency.fulfilled : true;

    setLoading(true);
    setInitialRetrieved(true);
    // used to retrieve initial options
    retrieveConnectionRecords({ dependencyValueChange: true, dependencyFulfilled });

    onOpen && onOpen(event);
  };

  // if user passes in "filterOptions" prop - we will filter them before rendering as options in Autocomplete
  const finalOptions = useMemo(() => {
    if (!manualFilterOptions) return [...options];

    const valueIsArray = Array.isArray(value);

    const valueAsArray = valueIsArray ? value : [value];

    // Separate options that are part of the value (value can be single or multiple depending on multiple prop)
    const optionsWithAndWithoutValue = options.reduce(
      (accumulated, current) => {
        const match = valueAsArray.find((value) => current.id === value.id);

        match ? accumulated.valueOptions.push(current) : accumulated.nonValueOptions.push(current);

        return accumulated;
      },
      {
        valueOptions: [],
        nonValueOptions: [],
      },
    );

    // only allow filtering of non-value options
    const filteredOptions: any[] = manualFilterOptions(optionsWithAndWithoutValue.nonValueOptions) as any[];

    // after filtering non-value options - combine with value options
    const combinedValueOptionsAndFilteredOptions = [...filteredOptions, ...optionsWithAndWithoutValue.valueOptions];

    return combinedValueOptionsAndFilteredOptions;
  }, [options, manualFilterOptions, autocompleteProps.groupBy]);

  const sortedFinalOptions = useMemo(() => {
    const groupBy: ConnectionAutocompleteProps<T, P>["groupBy"] = autocompleteProps.groupBy;

    let sortedFinalOptions = finalOptions;
    if (groupBy) {
      sortedFinalOptions = finalOptions.sort((a, b) => {
        // prioritize sorting by the group. If the groups are the same, sort based on label length
        if (groupBy(a) === groupBy(b)) {
          return a.autocompleteLabel?.length - b.autocompleteLabel.length;
        }

        return groupBy(a) > groupBy(b) ? 1 : -1;
      });
    } else if (inputValue && calculatedValue?.autocompleteLabel !== inputValue) {
      sortedFinalOptions = finalOptions.sort((a, b) => {
        // prioritize sorting based on label length. If they are the same length, sort alphabetically
        if (a.autocompleteLabel?.length === b.autocompleteLabel?.length) {
          return a.autocompleteLabel?.localeCompare(b.autocompleteLabel);
        }

        return a.autocompleteLabel?.length - b.autocompleteLabel.length;
      });
    } else {
      sortedFinalOptions = finalOptions.sort((a, b) => {
        // sort alphabetically
        return a.autocompleteLabel?.localeCompare(b.autocompleteLabel);
      });
    }

    return sortedFinalOptions;
  }, [finalOptions, autocompleteProps.groupBy, calculatedValue, inputValue]);

  const isAllSelected = useMemo(() => {
    if (!Array.isArray(calculatedValue)) return false;

    return sortedFinalOptions.length === calculatedValue.length;
  }, [sortedFinalOptions, calculatedValue]);

  const customRenderOption = useMemo(() => generateRenderOption({ renderOption, isAllSelected }), [renderOption, isAllSelected]);

  const generatedOnChange = useMemo(
    () =>
      generateOnChange({
        onChange,
        value,
        dispatchOptions,
        options: sortedFinalOptions,
        isAllSelected,
        selectAllOption,
      }),
    [value, onChange, dispatchOptions, sortedFinalOptions, selectAllOption],
  );

  //* Use Effects
  useEffect(() => {
    // if waiting to retrieve until interacted with - do not do initial retrieval
    if (lazyRetrieve) {
      setLoading(false);
      return;
    }

    const dependencyFulfilled = dependency?.fulfilled !== undefined ? dependency.fulfilled : true;

    // upon initial render - dependencyValueChange should be false. Once we have already rendered/retrieved, it would be true when this useEffect is triggered
    const dependencyValueChange = rendered ? true : false;

    // used to retrieve initial options
    retrieveConnectionRecords({ dependencyValueChange: dependencyValueChange, dependencyFulfilled });
  }, [dependency?.dependencyValue]);

  useEffect(() => {
    /* 
        When changing the value - the inputValue will be the label of that option triggering this useEffect
            We do not need to retrieve records when selecting an option we already have
    */
    const inputValueMatchesExistingOption = options.find((option) => option.autocompleteLabel === inputValue);

    // Only retrieve when there is a search value
    if (!inputValue || inputValueMatchesExistingOption) return;

    // debouncer to search records using inputValue when user stops typing
    const timeoutRequest = setTimeout(() => {
      (async () => {
        setLoading(true);
        await retrieveConnectionRecords();
      })();
    }, 500);

    return () => {
      clearTimeout(timeoutRequest);
    };
  }, [inputValue]);

  useEffect(() => {
    if (!calculatedValue) return;

    /* 
      Whenever the value changes - we need to combine with the existing options
        If we use the Create From Template feature - it is possible values will be set to options that don't exist
          so we must add the value to the existing options every time (dispatchOptions handles duplicates)
    */
    if (Array.isArray(value)) {
      dispatchOptions({
        type: OptionsActionTypes.ADD,
        payload: calculatedValue,
      });
    } else {
      dispatchOptions({
        type: OptionsActionTypes.ADD,
        payload: [calculatedValue],
      });
    }
  }, [calculatedValue]);

  useEffect(() => {
    //* Whenever options change - we will pass that through the setConnections parameter (if it exists)
    setConnections && setConnections(options);
  }, [options]);

  useEffect(() => {
    //* Whenever totalRecords count change - we will pass that through the setTotalCount parameter (if it exists)
    setTotalCount && setTotalCount(totalRecords);
  }, [totalRecords]);

  useEffect(() => {
    if (!additionalOptions?.length) return;

    dispatchOptions({ type: OptionsActionTypes.ADD, payload: additionalOptions });
  }, [additionalOptions]);

  useEffect(() => {
    setRendered(true);
  }, []);

  return (
    <Autocomplete
      {...autocompleteProps}
      getOptionLabel={(option: any) => option?.autocompleteLabel || ""}
      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
      renderInput={customRenderInput}
      renderOption={customRenderOption}
      value={calculatedValue}
      options={autocompleteProps.multiple && selectAllOption ? [generateSelectAllOption(isAllSelected), ...sortedFinalOptions] : sortedFinalOptions}
      inputValue={inputValue}
      disabled={disabled}
      onInputChange={(event, value) => {
        setInputValue(value);
      }}
      loading={loading}
      onChange={generatedOnChange}
      freeSolo={freeSolo}
      filterOptions={freeSolo ? filterOptions : undefined}
      onOpen={!lazyRetrieve && !onOpen ? undefined : customOnOpen}
      fullWidth
    />
  );
};

export { type ConnectionAutocompleteRef };

export default ConnectionAutocomplete;
